import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'

interface ConfirmModalProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning' | 'info'
  onConfirm: () => void
  onCancel: () => void
}

const variantStyles = {
  danger: {
    icon: '🗑️',
    confirmBg: 'bg-signal-red',
    confirmHover: 'hover:bg-signal-red/80',
  },
  warning: {
    icon: '⚠️',
    confirmBg: 'bg-signal-amber',
    confirmHover: 'hover:bg-signal-amber/80',
  },
  info: {
    icon: 'ℹ️',
    confirmBg: 'bg-signal-blue',
    confirmHover: 'hover:bg-signal-blue/80',
  },
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const styles = variantStyles[variant]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            className="fixed inset-0 bg-void/80 backdrop-blur-sm z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0.1 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="glass-card p-6 max-w-sm w-full shadow-xl">
              {/* Icon */}
              <div className="text-center mb-4">
                <span className="text-4xl">{styles.icon}</span>
              </div>

              {/* Content */}
              <h3 className="text-lg font-semibold text-star text-center mb-2">
                {title}
              </h3>
              <p className="text-sm text-star-muted text-center mb-6">
                {message}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-star-muted bg-void-50 border border-star-dim/20 hover:text-star hover:border-star-dim/40 transition-all"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className={clsx(
                    'flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all',
                    styles.confirmBg,
                    styles.confirmHover
                  )}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
